import * as d3 from "d3";
import gabonGeoJSON from "../../data/ga.json";

// Vérifie si une province est active dans la phase opérationnelle actuelle (Estuaire uniquement)
export const isProvinceOperational = (provinceIdOrName) => {
  return provinceIdOrName === "GA1" || provinceIdOrName === "Estuaire";
};

// Fonction pour obtenir la couleur de la province
// Estuaire est active (vert vibrant), les 8 autres provinces sont grisées pour refléter la non-fonctionnalité
export const getProvinceColor = (provinceIdOrName, pilotMode = true) => {
  if (pilotMode) {
    if (isProvinceOperational(provinceIdOrName)) {
      return "#15803d"; // Vert forêt vif & rassurant pour l'Estuaire (Phase Pilote Active)
    }
    return "#cbd5e1"; // Gris ardoise éteint (Slate 300) reflétant la non-activité / Phase 2
  }

  // Palette complète historique si nécessaire
  const colorsByID = {
    "GA1": "#15803d",
    "GA2": "#558B2F",
    "GA3": "#8BC34A",
    "GA4": "#689F38",
    "GA5": "#F9A825",
    "GA6": "#AFB42B",
    "GA7": "#9E9D24",
    "GA8": "#33691E",
    "GA9": "#C0CA33"
  };
  return colorsByID[provinceIdOrName] || "#cbd5e1";
};

// Fonction pour initialiser la carte avec effet de zoom dynamique sur la province sélectionnée
export const initializeMap = (
  isMobile,
  onProvinceClick,
  onProvinceHover,
  containerSelector = "#map",
  activeProvinceId = "GA1" // Par défaut centré et zoomé sur l'Estuaire
) => {
  // Dimensions adaptées au gabarit du Gabon (~1:1)
  const svgWidth = isMobile ? 340 : 580;
  const svgHeight = isMobile ? 380 : 540;
  const padding = isMobile ? 12 : 22;

  // Supprime tout contenu précédent dans le conteneur spécifié
  const container = d3.select(containerSelector);
  if (container.empty()) return null;
  container.selectAll("*").remove();

  const svg = container
    .append("svg")
    .attr("width", "100%")
    .attr("height", "100%")
    .attr("preserveAspectRatio", "xMidYMid meet")
    .attr("viewBox", `0 0 ${svgWidth} ${svgHeight}`)
    .style("margin", "0 auto")
    .style("display", "block")
    .style("max-width", "100%");

  // Projection D3 mathématiquement ajustée à 100% de la géométrie du Gabon (zéro coupure)
  const projection = d3
    .geoMercator()
    .fitExtent(
      [[padding, padding], [svgWidth - padding, svgHeight - padding]],
      gabonGeoJSON
    );

  const path = d3.geoPath().projection(projection);

  // Définitions de filtres SVG (Glow et Ombre portée pour le zoom)
  const defs = svg.append("defs");
  
  // Filtre de brillance néon
  const filter = defs.append("filter")
    .attr("id", "glow")
    .attr("x", "-50%")
    .attr("y", "-50%")
    .attr("width", "200%")
    .attr("height", "200%");
    
  filter.append("feGaussianBlur")
    .attr("stdDeviation", "3.5")
    .attr("result", "coloredBlur");
    
  const feMerge = filter.append("feMerge");
  feMerge.append("feMergeNode").attr("in", "coloredBlur");
  feMerge.append("feMergeNode").attr("in", "SourceGraphic");

  // Filtre d'élévation / ombre pour détacher la province zoomée
  const shadowFilter = defs.append("filter")
    .attr("id", "province-shadow")
    .attr("x", "-40%")
    .attr("y", "-40%")
    .attr("width", "180%")
    .attr("height", "180%");
  shadowFilter.append("feDropShadow")
    .attr("dx", "0")
    .attr("dy", "3")
    .attr("stdDeviation", "4")
    .attr("flood-color", "rgba(0, 34, 17, 0.45)");

  // Groupe principal recevant la transformation de zoom (scale + translate fluide)
  const mapGroup = svg.append("g")
    .attr("class", "map-zoom-group");

  // Créer un groupe pour les provinces
  const provinces = mapGroup.append("g")
    .attr("class", "provinces");

  // Groupe pour les labels (textes des provinces)
  const labelsGroup = mapGroup.append("g")
    .attr("class", "province-labels");

  // Calcul mathématique du zoom centré sur la province sélectionnée
  const getTransformForFeature = (feature) => {
    if (!feature) {
      return { x: 0, y: 0, k: 1 };
    }
    const bounds = path.bounds(feature);
    const [[x0, y0], [x1, y1]] = bounds;
    const dx = x1 - x0;
    const dy = y1 - y0;
    const x = (x0 + x1) / 2;
    const y = (y0 + y1) / 2;

    // Zoom fluide et proportionnel : la province remplit ~72% de la surface avec une marge agréable
    const maxZoom = isMobile ? 2.8 : 2.7;
    const minZoom = 1.55;
    const targetScale = Math.min(
      maxZoom,
      Math.max(minZoom, 0.74 / Math.max(dx / svgWidth, dy / svgHeight))
    );

    const translateX = svgWidth / 2 - targetScale * x;
    const translateY = svgHeight / 2 - targetScale * y;

    return { x: translateX, y: translateY, k: targetScale };
  };

  // Fonction d'animation du zoom vers une province donnée (ou dézoom total si null)
  const zoomToProvince = (targetIdOrFeature, duration = 650) => {
    let feature = null;
    if (targetIdOrFeature) {
      if (typeof targetIdOrFeature === "object" && targetIdOrFeature.geometry) {
        feature = targetIdOrFeature;
      } else {
        feature = gabonGeoJSON.features.find(
          f => f.properties.id === targetIdOrFeature ||
               f.properties.name === targetIdOrFeature
        );
      }
    }

    const { x, y, k } = getTransformForFeature(feature);
    const targetId = feature ? feature.properties.id : null;

    // Animation fluide sur le groupe principal
    mapGroup
      .transition()
      .duration(duration)
      .ease(d3.easeCubicOut)
      .attr("transform", `translate(${x}, ${y}) scale(${k})`);

    // Mise en relief visuel de la province ciblée
    provinces
      .selectAll("path")
      .transition()
      .duration(duration)
      .ease(d3.easeCubicOut)
      .attr("opacity", d => {
        if (!targetId) return isProvinceOperational(d.properties.id) ? 1 : 0.65;
        return d.properties.id === targetId ? 1 : 0.45;
      })
      .attr("stroke", d => {
        if (isProvinceOperational(d.properties.id)) return "#ffffff";
        return "#94a3b8";
      })
      .attr("stroke-width", d => {
        if (isProvinceOperational(d.properties.id)) {
          return isMobile ? (2.6 / k) : (3.2 / k);
        }
        return 0.8 / k;
      })
      .style("filter", d => {
        return isProvinceOperational(d.properties.id) ? "url(#province-shadow)" : "none";
      });

    // Adapter la taille et visibilité des labels de province
    labelsGroup
      .selectAll("text")
      .transition()
      .duration(duration)
      .attr("font-size", d => {
        const isEstuaire = isProvinceOperational(d.properties.id);
        const baseSize = isEstuaire ? (isMobile ? 9 : 11.5) : (isMobile ? (d.properties.id === "GA8" ? 6.5 : 7.2) : 9);
        return `${Math.max(5, baseSize / Math.sqrt(k))}px`;
      })
      .attr("opacity", d => {
        if (isProvinceOperational(d.properties.id)) return 1;
        return targetId ? 0.4 : 0.8;
      })
      .attr("font-weight", d => (isProvinceOperational(d.properties.id) ? 900 : 600))
      .style("stroke-width", d => {
        const isEstuaire = isProvinceOperational(d.properties.id);
        const base = isEstuaire ? 2.8 : 1.8;
        return `${base / Math.sqrt(k)}px`;
      });
  };

  // Configuration d'ajustement des étiquettes des 9 provinces du Gabon (avec rotation diagonale pour Ogooué-Maritime)
  const provinceLabelConfig = {
    GA1: { name: "Estuaire", rotate: 0, dx: 0, dy: 0 },
    GA2: { name: "Haut-Ogooué", rotate: 0, dx: 0, dy: 0 },
    GA3: { name: "Moyen-Ogooué", rotate: 0, dx: 0, dy: 0 },
    GA4: { name: "Ngounié", rotate: 0, dx: 0, dy: 0 },
    GA5: { name: "Nyanga", rotate: 0, dx: 0, dy: 0 },
    GA6: { name: "Ogooué-Ivindo", rotate: 0, dx: 0, dy: 0 },
    GA7: { name: "Ogooué-Lolo", rotate: 0, dx: 0, dy: 0 },
    GA8: { name: "Ogooué-Maritime", rotate: isMobile ? -50 : -46, dx: 0, dy: 0 },
    GA9: { name: "Wouleu-Ntem", rotate: 0, dx: 0, dy: 0 }
  };

  // Ajouter les provinces (Estuaire active en vert, autres grisées)
  provinces
    .selectAll("path")
    .data(gabonGeoJSON.features)
    .enter()
    .append("path")
    .attr("d", path)
    .attr("fill", d => getProvinceColor(d.properties.id))
    .attr("stroke", d => (isProvinceOperational(d.properties.id) ? "#ffffff" : "#94a3b8"))
    .attr("stroke-width", d => (isProvinceOperational(d.properties.id) ? (isMobile ? 2 : 2.5) : (isMobile ? 0.9 : 1.1)))
    .attr("class", d => `province-${d.properties.id}`)
    .style("filter", d => (isProvinceOperational(d.properties.id) ? "url(#province-shadow)" : "none"))
    .style("cursor", "pointer")
    .style("transition", "fill 0.25s ease, stroke 0.25s ease")
    .on("mouseover", function (event, d) {
      const isOp = isProvinceOperational(d.properties.id);
      if (isOp) {
        d3.select(this)
          .attr("fill", "#16a34a")
          .style("filter", "url(#glow)");
      } else {
        d3.select(this)
          .attr("fill", "#94a3b8")
          .attr("stroke", "#64748b");
      }
      
      if (onProvinceHover) {
        onProvinceHover(d.properties.id, isOp);
      }
    })
    .on("mouseout", function () {
      const provinceId = d3.select(this).datum().properties.id;
      const isOp = isProvinceOperational(provinceId);
      d3.select(this)
        .attr("fill", getProvinceColor(provinceId))
        .attr("stroke", isOp ? "#ffffff" : "#94a3b8")
        .style("filter", isOp ? "url(#province-shadow)" : "none");
      
      if (onProvinceHover) {
        onProvinceHover(null, false);
      }
    })
    .on("click", function (event, d) {
      const provinceId = d.properties.id;
      const isOp = isProvinceOperational(provinceId);
      
      if (isOp) {
        // Zoom in dynamique centré sur l'Estuaire
        zoomToProvince(d, 600);
      }

      if (onProvinceClick) {
        onProvinceClick(provinceId, d);
      }
    });

  // Inscrire le nom complet de chaque province directement dans sa zone géographique (Mobile & Desktop)
  labelsGroup
    .selectAll("text")
    .data(gabonGeoJSON.features)
    .enter()
    .append("text")
    .attr("class", d => `label-${d.properties.id}`)
    .attr("transform", d => {
      const centroid = path.centroid(d);
      const cfg = provinceLabelConfig[d.properties.id] || { rotate: 0, dx: 0, dy: 0 };
      const x = centroid[0] + cfg.dx;
      const y = centroid[1] + cfg.dy;
      return `translate(${x}, ${y}) rotate(${cfg.rotate})`;
    })
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("fill", d => (isProvinceOperational(d.properties.id) ? "#ffffff" : "#64748b"))
    .attr("font-size", d => {
      const isEstuaire = isProvinceOperational(d.properties.id);
      return isEstuaire
        ? (isMobile ? "8.8px" : "11px")
        : (isMobile ? (d.properties.id === "GA8" ? "6.8px" : "7.2px") : "9px");
    })
    .attr("font-weight", d => (isProvinceOperational(d.properties.id) ? 900 : 600))
    .attr("letter-spacing", "-0.15px")
    .attr("pointer-events", "none")
    .style("paint-order", "stroke fill")
    .style("stroke", d => (isProvinceOperational(d.properties.id) ? "#01321d" : "#f8fafc"))
    .style("stroke-width", d => (isProvinceOperational(d.properties.id) ? (isMobile ? "2.6px" : "3.5px") : (isMobile ? "1.6px" : "2px")))
    .style("stroke-linecap", "round")
    .style("stroke-linejoin", "round")
    .text(d => {
      const cfg = provinceLabelConfig[d.properties.id];
      return cfg ? cfg.name : d.properties.name;
    });

  // Zoomer immédiatement sur la province de l'Estuaire dès le montage
  const initialZoomTarget = activeProvinceId || "GA1";
  zoomToProvince(initialZoomTarget, 550);

  return { zoomToProvince, svg };
};
